import './styles.css';

// import CharacterCount from "@tiptap/extension-character-count";

import { Button } from '@/components/ui/button';
import { CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { type Deal, type Reply } from '@/models/registry';
import { dealStore, replyStore } from '@/models/stores';
import type { Reply as ReplyType } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEmpty, trim } from 'lodash';
import { PaperclipIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo } from 'react';
import { getUserMentionIds } from './get-user-mentions-ids';
import PaddockBubbleMenu from './paddock-bubble-menu';
import PdkEditorContent from './pdk-editor-content';
import { usePaddockEditor } from './use-paddock-editor';

export const OPTIMISTIC_REPLY_ID_PREFIX = 'optimistic-reply-';

// TODO (jimmy) TECH DEBT: this is a copy of AddReplyTiptap

const EditReplyTiptap = ({
  dealId,
  reply,
  setIsEditingReply,
  className,
}: {
  dealId: Deal['id'];
  reply: Reply;
  setIsEditingReply: (isEditing: boolean) => void;
  className?: string;
}) => {
  // TODO (jimmy) TECH DEBT: Consolidate this with MessageTipTap
  // into a useParseContent hook
  const initialContent = useMemo(() => {
    try {
      // DEV NOTE: comments and replies are stringified json
      // thus we need to parse it
      return reply.content;
    } catch (error) {
      console.error('Error parsing initialContent', error);
      return '';
    }
  }, [reply.content]);

  const { editor } = usePaddockEditor({
    initialContent,
  });

  const user = useUser();

  const resetEditor = useCallback(() => {
    editor?.commands.setContent('');
  }, [editor]);

  const editReply = useMutation({
    onMutate: async ({ reply }: { reply: ReplyType }) => {
      const replyV3 = {
        id: reply.id,
        deal_id: dealId,
        created_at: reply.created_at,
        updated_at: dayjs().toISOString(),
        content: reply.content,
        edited: true,
        comment_id: reply.comment_id,
        text: reply.text,
        mentions: reply.mentions,
        user_id: user.id,
      };
      dealStore.replaceReplyAndDedupeV3({
        idToReplace: reply.id,
        reply: replyV3,
        dealId,
      });
    },
    mutationFn: async ({ reply }: { reply: ReplyType }) => {
      if (!reply) return;

      const response = await pdkAxios
        .post(
          `/deals/${dealId}/comments/v3/${reply.comment_id}/reply/${reply.id}/edit`,
          {
            // /:id/comments
            content: reply.content,
            text: reply.text,
            mentions: reply.mentions,
          },
        )
        .catch((e) => {
          console.error('Error editing polymorphic reply', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      setIsEditingReply(false);
      resetEditor();
    },
    onError: (error, variables) => {
      // UX Note: restore the content of the editor
      editor?.commands.setContent(variables?.reply?.content);
      dealStore.replaceReplyAndDedupeV3({
        idToReplace: variables?.reply?.id,
        reply: variables?.reply,
        dealId,
      });
      toast({
        title: 'Error editing reply',
        description: error.message,
      });
      console.error('Error editing polymorphic reply', error, variables);
    },
  });

  const handleSubmitComment = () => {
    // TECH DEBT: handle trimming whitespace from `content`
    // this requires writing a function that remove hardBreaks
    // and texts that are empty
    // and trimming whitespace from the text
    const content = editor?.getJSON();

    const text = editor?.getText() || '';

    if (isEmpty(trim(text))) {
      return;
    }

    const mentionIds = getUserMentionIds(editor);

    const payload = {
      reply: {
        id: reply.id,
        comment_id: reply.comment_id,
        created_at: reply.created_at,
        updated_at: dayjs().toISOString(),
        content: content || {},
        text,
        mentions: mentionIds,
        edited: true,
        user_id: user.id,
      },
    };

    if (!!replyStore.getById(reply.id)) {
      editReply.mutate(payload);
    }
  };

  const isExpanded = true;

  useEffect(() => {
    editor?.commands.focus('end');
  }, [editor]);

  const handleClose = useCallback(() => {
    setIsEditingReply(false);
  }, [setIsEditingReply]);

  return (
    <div className={className}>
      <div className="flex flex-col gap-4">
        <CardHeader className="p-0">
          <CardDescription>
            <PaddockBubbleMenu editor={editor} />

            <PdkEditorContent
              editor={editor}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' && e.metaKey) ||
                  (e.key === 'Enter' && e.ctrlKey)
                ) {
                  e.preventDefault();
                  handleSubmitComment();
                }

                if (e.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
                  handleClose();
                }
              }}
            />
          </CardDescription>
        </CardHeader>
        <CardFooter className="p-0">
          <motion.div
            className="flex flex-row w-full justify-between gap-2 h-10 items-center py-2"
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: isExpanded ? 'auto' : 0,
              opacity: isExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <PaperclipIcon
              className={cn(
                'size-4 cursor-pointer text-gray-500 hover:fill-gray-100',
                {},
              )}
              onClick={() => {
                toast({
                  title: 'Feature not implemented yet',
                  description:
                    "Slack us if you want the 'comments attachments' feature",
                });
              }}
            />

            <div className="flex flex-row gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={handleClose}>
                    Cancel
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row gap-1 items-center">
                  <p>Cancel</p>
                  <KeyboardKeyIcon>
                    {HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSubmitComment}
                  >
                    Save
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row gap-1 items-center">
                  <p>Submit Comment</p>
                  <KeyboardKeyIcon>
                    {HOTKEYS.DEAL_SUBMIT_COMMENT}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>
            </div>
          </motion.div>
        </CardFooter>
      </div>
    </div>
  );
};

export default EditReplyTiptap;
